import { useRef } from 'react';
import clsx from 'clsx';
import {
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  Paper,
  Select,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  CheckCircleRounded as ActiveIcon,
  EcoRounded as EcoIcon,
  EmojiNatureRounded as WildIcon,
  FlagRounded as FrenchIcon,
  PanoramaRounded as PanoramaIcon,
  PhotoCameraRounded as PhotoCameraIcon,
  RoomRounded as LocalIcon,
  SpaRounded as OrganicIcon,
  StarRounded as TopIcon,
} from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';

import { Product } from '../../types/product.type';
import { PRODUCT_SIZES } from '../../types/product-sizes.type';
import { UpdateProductRequestDTO } from '../../types/dtos/update-product.dto';
import { useProductEditForm } from '../../hooks/use-product-edit-form.hook';
import { buildProductUpdatePayload } from '../../utils/build-product-update-payload.util';
import { useUploadProductImage } from '../../hooks/upload-product-image.hook';
import { useAdminGetCategories } from '../../../categories/hooks/admin-get-categories.hook';
import { useAdminGetSubCategories } from '../../../categories/hooks/admin-get-sub-categories.hook';
import { useAdminGetTaxes } from '../../../taxes/hooks/admin-get-taxes.hook';
import { useAdminGetTags } from '../../../tags/hooks/admin-get-tags.hook';
import { useAdminGetWeightUnities } from '../../../weight-unities/hooks/admin-get-weight-unities.hook';
import { Category } from '../../../categories/types/category.type';
import { SubCategory } from '../../../categories/types/sub-category.type';
import { TaxWithUsage } from '../../../taxes/types/tax.type';
import { Tag } from '../../../tags/types/tag.type';
import { WeightUnity } from '../../../weight-unities/types/weight-unity.type';
import { PRODUCT_IMAGE_PATH } from '../../constants/products-app-paths.constants';
import RichTextEditor from '../../../shared/components/Forms/RichTextEditor.component';

type Props = {
  product: Product;
  isSubmitting: boolean;
  updateProduct: (productData: UpdateProductRequestDTO) => Promise<void>;
  refreshProduct: () => Promise<void>;
};

const NAME_MIN_LENGTH = 2;
const REFERENCE_MIN_LENGTH = 2;

const useStyles = makeStyles((theme) => ({
  root: {
    '& h4': {
      margin: '0 0 0.5rem 0',
      opacity: 0.7,
    },

    '& .MuiButton-root': {
      textTransform: 'none',
    },

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  actionsHeader: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '0.75rem',
  },
  chipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  chip: {
    cursor: 'pointer',
    borderRadius: 4,
    fontWeight: 600,
    fontSize: 12,

    '& .MuiChip-icon': {
      fontSize: 14,
      marginLeft: 6,
      marginRight: -4,
    },
  },
  chipSelected: {
    border: '1px solid transparent',
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,

    '& .MuiChip-icon': {
      color: theme.palette.admin.primary.contrastText,
    },

    '&:hover, &:focus': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
  chipDivider: {
    width: 1,
    height: 20,
    backgroundColor: '#ccc',
    marginLeft: '0.25rem',
    marginRight: '0.25rem',
  },
  saveButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
  paperContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem 0.75rem',
    height: 'calc(100% - 2rem)',
  },
  identityTop: {
    display: 'flex',
    gap: '1rem',
  },
  imageContainer: {
    position: 'relative',
    minWidth: 150,
    width: 150,
    height: 150,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: '#fafafa',
    border: '1px solid #e0e0e0',
    cursor: 'pointer',

    '&:hover $imageOverlay': {
      opacity: 1,
    },
  },
  imagePreview: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  imageIcon: {
    fontSize: 40,
    color: '#ccc',
  },
  imageOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(34, 40, 49, 0.55)',
    color: '#ffffff',
    opacity: 0,
    transition: 'opacity 0.2s',
  },
  identityFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    flex: 1,
  },
  row: {
    display: 'flex',
    gap: '1rem',

    '& > *': {
      flex: 1,
    },
  },
}));

export default function ProductInformationsTab({ product, isSubmitting, updateProduct, refreshProduct }: Props) {
  const classes = useStyles();
  const {
    isEditing,
    updatedProduct,
    handleFieldChange,
    handleCategoryChange,
    handleSubCategoryChange,
    handleCancelEditing,
    handleSave,
  } = useProductEditForm(product);

  const { uploadProductImage, isSubmitting: isUploading } = useUploadProductImage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { categories } = useAdminGetCategories();
  const { subCategories } = useAdminGetSubCategories({ categoryId: updatedProduct.category?.id });
  const { taxes } = useAdminGetTaxes();
  const { tags } = useAdminGetTags();
  const { weightUnities } = useAdminGetWeightUnities();

  const sortedCategories = [...categories].sort((first, second) => first.name.localeCompare(second.name));
  const sortedSubCategories = [...subCategories].sort((first, second) => first.name.localeCompare(second.name));

  const selectedCategory = sortedCategories.find((category) => category.id === updatedProduct.category?.id) ?? null;
  const selectedSubCategory =
    sortedSubCategories.find((subCategory) => subCategory.id === updatedProduct.subCategory?.id) ?? null;
  const selectedTax = taxes.find((tax) => tax.id === updatedProduct.tax?.id) ?? null;
  const selectedWeightUnity =
    weightUnities.find((weightUnity) => weightUnity.id === updatedProduct.weight?.unity?.id) ?? null;

  const isArchived = Boolean(product.archivedAt);
  const trimmedName = updatedProduct.name.trim();
  const isNameInvalid = trimmedName.length < NAME_MIN_LENGTH;
  const nameHelperText = trimmedName === '' ? 'Le nom est requis' : 'Le nom doit contenir au moins 2 caractères';
  const isReferenceEdited = updatedProduct.reference !== product.reference;
  const isReferenceInvalid = isReferenceEdited && (updatedProduct.reference ?? '').trim().length < REFERENCE_MIN_LENGTH;
  const isSubCategoryInvalid = !updatedProduct.subCategory?.id;
  const isPriceInvalid = updatedProduct.price <= 0;
  const isWeightInvalid = (updatedProduct.weight?.quantity ?? 0) <= 0;
  const isStockInvalid = updatedProduct.stock < 0;
  const hasValidationErrors =
    isNameInvalid || isReferenceInvalid || isSubCategoryInvalid || isPriceInvalid || isWeightInvalid || isStockInvalid;

  const handleImageSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const uploadedKey = await uploadProductImage(product.id, file);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (uploadedKey) await refreshProduct();
  };

  const saveEditedFields = async (editedFields: Partial<Product>) => {
    const productData = buildProductUpdatePayload(editedFields);
    if (Object.keys(productData).length === 0) return;

    await updateProduct(productData);
  };

  const toggleLabel = (labelKey: 'organic' | 'wild' | 'french' | 'native') =>
    handleFieldChange({
      field: 'labels',
      value: { ...updatedProduct.labels, [labelKey]: !updatedProduct.labels?.[labelKey] },
    });

  return (
    <section className={classes.root}>
      <Grid container spacing={2}>
        <Grid item xs={12} className={classes.actionsHeader}>
          <Button size="small" variant="text" onClick={handleCancelEditing} disabled={!isEditing || isSubmitting}>
            Annuler
          </Button>
          <Button
            size="small"
            variant="contained"
            className={classes.saveButton}
            onClick={() => handleSave(saveEditedFields)}
            disabled={!isEditing || isSubmitting || hasValidationErrors}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          >
            {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </Grid>

        <Grid item xs={12} md={6}>
          <section>
            <h4>Identité</h4>
            <Paper className={classes.paperContent}>
              <div className={classes.identityTop}>
                <div
                  className={classes.imageContainer}
                  title="Changer l'image"
                  onClick={() => !isUploading && fileInputRef.current?.click()}
                >
                  {isUploading ? (
                    <CircularProgress size={40} />
                  ) : updatedProduct.photo ? (
                    <img
                      src={PRODUCT_IMAGE_PATH(updatedProduct.photo)}
                      alt={updatedProduct.name}
                      className={classes.imagePreview}
                    />
                  ) : (
                    <PanoramaIcon className={classes.imageIcon} />
                  )}
                  {!isUploading && (
                    <div className={classes.imageOverlay}>
                      <PhotoCameraIcon />
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  hidden
                  onChange={handleImageSelected}
                />

                <div className={classes.identityFields}>
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    required
                    label="Nom"
                    error={isNameInvalid}
                    helperText={isNameInvalid ? nameHelperText : undefined}
                    value={updatedProduct.name}
                    onChange={(event) => handleFieldChange({ field: 'name', value: event.target.value })}
                  />
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    label="Référence"
                    error={isReferenceInvalid}
                    helperText={isReferenceInvalid ? 'La référence doit contenir au moins 2 caractères' : undefined}
                    value={updatedProduct.reference}
                    onChange={(event) => handleFieldChange({ field: 'reference', value: event.target.value })}
                  />
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    label="Origine"
                    value={updatedProduct.origin ?? ''}
                    onChange={(event) => handleFieldChange({ field: 'origin', value: event.target.value })}
                  />
                </div>
              </div>
              <RichTextEditor
                value={updatedProduct.description}
                onChange={(html) => handleFieldChange({ field: 'description', value: html })}
                placeholder="Description du produit"
              />
            </Paper>
          </section>
        </Grid>

        <Grid item xs={12} md={6}>
          <section>
            <h4>Caractéristiques</h4>
            <Paper className={classes.paperContent}>
              <div className={classes.chipRow}>
                <Chip
                  className={clsx(classes.chip, updatedProduct.isActive && classes.chipSelected)}
                  label="Actif"
                  icon={<ActiveIcon />}
                  size="small"
                  variant={updatedProduct.isActive ? 'default' : 'outlined'}
                  disabled={isArchived}
                  onClick={() => handleFieldChange({ field: 'isActive', value: !updatedProduct.isActive })}
                />
                <div className={classes.chipDivider} />
                <Chip
                  className={clsx(classes.chip, updatedProduct.labels?.organic && classes.chipSelected)}
                  label="Bio"
                  icon={<OrganicIcon />}
                  size="small"
                  variant={updatedProduct.labels?.organic ? 'default' : 'outlined'}
                  onClick={() => toggleLabel('organic')}
                />
                <Chip
                  className={clsx(classes.chip, updatedProduct.labels?.wild && classes.chipSelected)}
                  label="Sauvage"
                  icon={<WildIcon />}
                  size="small"
                  variant={updatedProduct.labels?.wild ? 'default' : 'outlined'}
                  onClick={() => toggleLabel('wild')}
                />
                <Chip
                  className={clsx(classes.chip, updatedProduct.labels?.french && classes.chipSelected)}
                  label="Français"
                  icon={<FrenchIcon />}
                  size="small"
                  variant={updatedProduct.labels?.french ? 'default' : 'outlined'}
                  onClick={() => toggleLabel('french')}
                />
                <Chip
                  className={clsx(classes.chip, updatedProduct.labels?.native && classes.chipSelected)}
                  label="Local"
                  icon={<LocalIcon />}
                  size="small"
                  variant={updatedProduct.labels?.native ? 'default' : 'outlined'}
                  onClick={() => toggleLabel('native')}
                />
                <div className={classes.chipDivider} />
                <Chip
                  className={clsx(classes.chip, updatedProduct.isTop && classes.chipSelected)}
                  label="Top"
                  icon={<TopIcon />}
                  size="small"
                  variant={updatedProduct.isTop ? 'default' : 'outlined'}
                  onClick={() => handleFieldChange({ field: 'isTop', value: !updatedProduct.isTop })}
                />
                <Chip
                  className={clsx(classes.chip, updatedProduct.isEco && classes.chipSelected)}
                  label="Éco"
                  icon={<EcoIcon />}
                  size="small"
                  variant={updatedProduct.isEco ? 'default' : 'outlined'}
                  onClick={() => handleFieldChange({ field: 'isEco', value: !updatedProduct.isEco })}
                />
              </div>
              <div className={classes.row}>
                <Autocomplete
                  fullWidth
                  size="small"
                  value={selectedCategory}
                  options={sortedCategories}
                  groupBy={(option) => option.name[0].toUpperCase()}
                  getOptionLabel={(option) => option.name}
                  getOptionSelected={(option, selected) => option.id === selected.id}
                  onChange={(event, selectedOption: Category | null) => handleCategoryChange(selectedOption)}
                  renderInput={(params) => <TextField {...params} size="small" variant="outlined" label="Rubrique" />}
                />
                <Autocomplete
                  fullWidth
                  size="small"
                  value={selectedSubCategory}
                  disabled={!selectedCategory}
                  options={sortedSubCategories}
                  groupBy={(option) => option.name[0].toUpperCase()}
                  getOptionLabel={(option) => option.name}
                  getOptionSelected={(option, selected) => option.id === selected.id}
                  onChange={(event, selectedOption: SubCategory | null) => handleSubCategoryChange(selectedOption)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      variant="outlined"
                      label="Sous-rubrique"
                      error={isSubCategoryInvalid}
                      helperText={isSubCategoryInvalid ? 'La sous-rubrique est requise' : undefined}
                    />
                  )}
                />
              </div>

              <div className={classes.row}>
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  type="number"
                  label="Prix"
                  error={isPriceInvalid}
                  helperText={isPriceInvalid ? 'Le prix doit être supérieur à 0' : undefined}
                  value={updatedProduct.price}
                  onChange={(event) => handleFieldChange({ field: 'price', value: Number(event.target.value) })}
                  InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                />
                <Autocomplete
                  fullWidth
                  size="small"
                  value={selectedTax}
                  options={[...taxes].sort((first, second) => first.name.localeCompare(second.name))}
                  getOptionLabel={(option) => option.name}
                  getOptionSelected={(option, selected) => option.id === selected.id}
                  onChange={(event, selectedOption: TaxWithUsage | null) => {
                    if (selectedOption) {
                      handleFieldChange({
                        field: 'tax',
                        value: { id: selectedOption.id, name: selectedOption.name, amount: selectedOption.amount },
                      });
                    }
                  }}
                  renderInput={(params) => <TextField {...params} size="small" variant="outlined" label="TVA" />}
                />
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel>Taille</InputLabel>
                  <Select
                    native
                    label="Taille"
                    value={updatedProduct.size}
                    onChange={(event) => handleFieldChange({ field: 'size', value: event.target.value as string })}
                  >
                    {Object.values(PRODUCT_SIZES).map((productSize) => (
                      <option key={productSize} value={productSize}>
                        {productSize}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <div className={classes.row}>
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  type="number"
                  label="Poids (quantité)"
                  error={isWeightInvalid}
                  helperText={isWeightInvalid ? 'Le poids doit être supérieur à 0' : undefined}
                  value={updatedProduct.weight?.quantity ?? ''}
                  onChange={(event) =>
                    handleFieldChange({
                      field: 'weight',
                      value: { ...updatedProduct.weight, quantity: Number(event.target.value) },
                    })
                  }
                />
                <Autocomplete
                  fullWidth
                  size="small"
                  value={selectedWeightUnity}
                  options={[...weightUnities].sort((first, second) => first.name.localeCompare(second.name))}
                  getOptionLabel={(option) => `${option.name} (${option.abbreviation})`}
                  getOptionSelected={(option, selected) => option.id === selected.id}
                  onChange={(event, selectedOption: WeightUnity | null) => {
                    if (selectedOption) {
                      handleFieldChange({
                        field: 'weight',
                        value: {
                          ...updatedProduct.weight,
                          unity: {
                            id: selectedOption.id,
                            name: selectedOption.name,
                            abbreviation: selectedOption.abbreviation,
                          },
                        },
                      });
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" variant="outlined" label="Unité de poids" />
                  )}
                />
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  type="number"
                  label="Stock"
                  error={isStockInvalid}
                  helperText={isStockInvalid ? 'Le stock ne peut pas être négatif' : undefined}
                  value={updatedProduct.stock}
                  onChange={(event) => handleFieldChange({ field: 'stock', value: Number(event.target.value) })}
                />
              </div>

              <Autocomplete
                multiple
                fullWidth
                size="small"
                value={updatedProduct.tags ?? []}
                options={[...tags].sort((first, second) => first.name.localeCompare(second.name))}
                getOptionLabel={(option) => option.name}
                getOptionSelected={(option, selected) => option.id === selected.id}
                onChange={(event, selectedOptions: Tag[]) =>
                  handleFieldChange({ field: 'tags', value: selectedOptions })
                }
                renderInput={(params) => <TextField {...params} size="small" variant="outlined" label="Tags" />}
              />
            </Paper>
          </section>
        </Grid>
      </Grid>
    </section>
  );
}
